import React, { useEffect, useState } from 'react';
import './Footer.scss';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { NavLink as NavL } from 'react-bootstrap';
import {
  addSubscribeEmail,
  resetSubscribeEmail,
  setSelectedUnit,
} from '../../../redux/slices/HomeSlice';
import { useDispatch, useSelector } from 'react-redux';
import { UnknownAction } from '@reduxjs/toolkit';
import { ToastType, showToastMessage } from '../../../utils/ToastOptionCommon';
import Logo from '../../../assets/images/grhamDeal-logo-slogan.png';
import { popularPropertySearch } from '../../../constants/Property';
import { loggedInUserState } from '../../../redux/slices/LoginSlice';

function Footer(props: any) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userData = useSelector(loggedInUserState);
  const [email, setEmail] = useState<string>('');
  const [showDonationModal, setShowDonationModal] = useState(false);
  const [donationAmount, setDonationAmount] = useState('');
  const [donationLoading, setDonationLoading] = useState(false);

  const subscribeEmailState = useSelector(
    (state: any) => state.home.subscribeEmail
  );

  useEffect(() => {
    if (userData?.userEmail) {
      setEmail(userData?.userEmail);
    }
  }, [userData?.userEmail]);

  useEffect(() => {
    if (subscribeEmailState?.success) {
      showToastMessage('You have successfully subscribed', ToastType.Success);
      dispatch(resetSubscribeEmail(null));
      setEmail('');
    }
  }, [subscribeEmailState?.success, dispatch]);

  useEffect(() => {
    if (subscribeEmailState?.error) {
      showToastMessage(subscribeEmailState.error, ToastType.Error);
      dispatch(resetSubscribeEmail(null));
    }
  }, [subscribeEmailState?.error, dispatch]);

  const subscribeEmail = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      showToastMessage('Please enter a valid email address', ToastType.Error);
      return;
    }
    dispatch(
      addSubscribeEmail({
        body: { email: email },
      }) as unknown as UnknownAction
    );
  };

  const startDonation = async () => {
    const amount = Number(donationAmount);

    if (!Number.isFinite(amount) || amount < 1) {
      showToastMessage(
        'Please enter a valid amount of at least ₹1.',
        ToastType.Error
      );
      return;
    }

    try {
      setDonationLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/payment/phonepe/donation/createOrder`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(localStorage.getItem('accessToken')
              ? {
                  Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
                }
              : {}),
          },
          body: JSON.stringify({ orderId: `DONATION-${Date.now()}`, amount }),
        }
      );
      const order = await response.json();

      if (!response.ok || !order?.url) {
        throw new Error(order?.message || 'Unable to open PhonePe payment.');
      }

      window.location.href = order.url;
    } catch (error: any) {
      showToastMessage(
        error.message || 'Unable to start donation.',
        ToastType.Error
      );
    } finally {
      setDonationLoading(false);
    }
  };

  if (
    location.pathname.includes('/dashboard') ||
    location.pathname.includes('/search-properties')
  ) {
    return null;
  }

  return (
    <footer>
      <div className="container">
        {showDonationModal && (
          <div className="donation-modal-backdrop" role="presentation">
            <div
              className="donation-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="donation-title"
            >
              <button
                type="button"
                className="donation-modal-close"
                onClick={() => setShowDonationModal(false)}
                aria-label="Close donation dialog"
              >
                &times;
              </button>
              <h2 id="donation-title">Support GrhamDeal</h2>
              <p>Enter an amount to continue securely with PhonePe.</p>
              <label htmlFor="donation-amount">Donation amount</label>
              <div className="donation-amount-input">
                <span>₹</span>
                <input
                  id="donation-amount"
                  type="number"
                  min="1"
                  step="1"
                  value={donationAmount}
                  onChange={(event) => setDonationAmount(event.target.value)}
                  placeholder="Enter amount"
                  autoFocus
                />
              </div>
              <button
                type="button"
                className="donation-submit"
                onClick={startDonation}
                disabled={donationLoading}
              >
                {donationLoading ? 'Opening PhonePe...' : 'Continue to PhonePe'}
              </button>
            </div>
          </div>
        )}

        <div className="footer-grid">
          <div className="footer-column footer-brand">
            <img className="logo" src={Logo} alt="GrhamDeal Logo" />
            <div className="support-grhamdeal">
              <p>
                Help us keep GrhamDeal growing and make property renting easier
                for everyone.
              </p>
              {/* <button type="button" onClick={() => setShowDonationModal(true)}>
                ❤️ Support GrhamDeal
              </button> */}
            </div>
          </div>

          <div className="footer-column">
            <h2>Terms & Policies</h2>
            <ul>
              <li>
                <NavLink to="/terms-and-conditions">
                  Terms And Conditions
                </NavLink>
              </li>
              <li>
                <NavLink to="/privacy-policy">Privacy Policy</NavLink>
              </li>
              <li>
                <NavLink to="/refund-policy">Refund Policy</NavLink>
              </li>
              <li>
                <NavLink to="/disclaimer">Disclaimer</NavLink>
              </li>
            </ul>
          </div>

          <div className="footer-column">
            <h2>Popular Searches</h2>
            <ul>
              {popularPropertySearch.map((e) => (
                <li key={e.id}>
                  <a
                    href="#/"
                    onClick={(evt) => {
                      evt.preventDefault();
                      dispatch(
                        setSelectedUnit({
                          unit: { Id: e.id, Name: e.label, Value: e.id },
                        })
                      );
                      if (location.pathname === '/') {
                        window.scrollTo(0, 0);
                      } else {
                        navigate('/');
                      }
                    }}
                  >
                    {e.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="footer-column">
            <h2>Popular Links</h2>
            <ul>
              <li>
                <NavLink to="/contact-us">Contact us</NavLink>
              </li>
              <li>
                <NavLink to="/blog">Blog</NavLink>
              </li>
              <li>
                <NavLink to="/faq">FAQ</NavLink>
              </li>
            </ul>
          </div>
        </div>

        <div className="footer-community">
          <div className="community-row">
            <div className="community-social">
              <h2>Follow Us</h2>
              <ul className="social-share">
                <li>
                  <a
                    href="https://www.facebook.com/profile.php?id=61580425527343"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Visit our Facebook profile"
                  >
                    <svg viewBox="0 0 11 20" fill="none">
                      <path
                        d="M3.0753 19.5952V19.1382C3.0753 16.6462 3.0673 14.1552 3.0833 11.6632C3.0833 11.2912 2.9783 11.1802 2.6103 11.1942C1.8743 11.2212 1.1373 11.1942 0.4003 11.2072C0.1063 11.2132 0.000299943 11.1172 0.000299943 10.8152C0.0162999 9.91525 0.0182999 9.01524 0.000299943 8.12324C-0.00670006 7.81124 0.1083 7.73624 0.4003 7.74224C1.1233 7.75824 1.8473 7.74724 2.5703 7.74724H3.0463C3.0583 7.53824 3.0693 7.37124 3.0763 7.20324C3.1233 6.04224 3.0763 4.86724 3.2383 3.72124C3.36974 2.85828 3.78989 2.06537 4.43012 1.472C5.07035 0.878622 5.89285 0.519843 6.7633 0.454244C7.20084 0.396918 7.64215 0.373515 8.0833 0.384244C8.8013 0.423244 9.5173 0.520244 10.2593 0.595244V3.54324C9.7023 3.54324 9.1463 3.53424 8.5903 3.54324C8.21155 3.52412 7.83763 3.63494 7.53046 3.85736C7.2233 4.07977 7.00132 4.40044 6.9013 4.76624C6.83684 5.01199 6.80524 5.26519 6.8073 5.51924C6.7943 6.09524 6.8303 6.67324 6.7933 7.24624C6.7653 7.67324 6.9323 7.76724 7.3233 7.75424C8.1013 7.72824 8.8743 7.74524 9.6513 7.74824C9.7953 7.74824 9.9383 7.76124 10.1273 7.77124C10.0013 8.62824 9.9013 9.44924 9.7553 10.2622C9.5863 11.2052 9.5683 11.2022 8.6143 11.2022C8.1593 11.2022 7.7023 11.2232 7.2483 11.1952C6.8933 11.1732 6.7913 11.2952 6.7933 11.6482C6.8073 14.1132 6.8003 16.5782 6.8003 19.0432C6.8003 19.6182 6.8003 19.6182 6.2443 19.6182C5.33363 19.6182 4.42263 19.6182 3.5113 19.6182C3.3813 19.6182 3.2513 19.6062 3.0753 19.5972"
                        fill="#1877F2"
                      />
                    </svg>
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.instagram.com/grhamdeal/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Visit our Instagram profile"
                  >
                    <svg viewBox="0 0 20 20" fill="none">
                      <path
                        d="M10 1.802c2.67 0 2.987.01 4.042.059 2.71.123 3.976 1.409 4.1 4.099.048 1.054.057 1.37.057 4.04 0 2.672-.01 2.988-.058 4.042-.124 2.687-1.387 3.975-4.1 4.099-1.054.048-1.37.058-4.041.058-2.67 0-2.987-.01-4.042-.058-2.717-.124-3.977-1.416-4.1-4.1-.048-1.054-.058-1.37-.058-4.041 0-2.67.01-2.986.058-4.04.124-2.69 1.387-3.977 4.1-4.1 1.055-.048 1.37-.058 4.042-.058ZM10 0C7.284 0 6.944.012 5.877.06 2.246.227.228 2.242.06 5.877.01 6.944 0 7.284 0 10s.012 3.056.06 4.123c.167 3.632 2.182 5.65 5.817 5.817 1.067.048 1.407.06 4.123.06s3.056-.012 4.123-.06c3.629-.167 5.652-2.182 5.816-5.817.05-1.067.061-1.407.061-4.123s-.012-3.056-.06-4.123C19.773 2.245 17.758.228 14.123.06 13.056.01 12.716 0 10 0Zm0 4.865a5.135 5.135 0 1 0 0 10.27 5.135 5.135 0 0 0 0-10.27Zm0 8.468a3.333 3.333 0 1 1 0-6.666 3.333 3.333 0 0 1 0 6.666Zm5.338-9.87a1.2 1.2 0 1 0 0 2.4 1.2 1.2 0 0 0 0-2.4Z"
                        fill="#E4405F"
                      />
                    </svg>
                  </a>
                </li>
              </ul>
            </div>

            <div className="newsletter">
              <h3>Subscribe to Newsletter</h3>
              <p>Get the latest property deals directly in your inbox.</p>
              <form onSubmit={subscribeEmail} className="input-group">
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <button type="submit" className="btn" aria-label="Subscribe">
                  <svg width="14" height="14" viewBox="0 0 24 24">
                    <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                  </svg>
                </button>
              </form>
            </div>
          </div>
        </div>

        <div className="copyright">
          <p>
            © {new Date().getFullYear()} Kaundal Ventures (Legal Entity: Preeti
            Rai), All Rights Reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
